/**
 * feat-259 — the ONE auto-save mechanism behind all five curriculum item panels.
 *
 * The panels (QuestionDetailForm ×2 kinds, RehearsalEditor, VideoDetailForm,
 * ReadingDetailForm) used to each own a `handleSave` wired to a Save button.
 * This hook replaces all of them. Five hand-rolled implementations would drift,
 * and the acceptance criteria are parametrised over all five types precisely
 * because that is the failure mode (spec §Acceptance tests AT11).
 *
 * What it owns, and why each piece is here rather than in a panel:
 *
 *  • DIRTY TRACKING PER FIELD, against a baseline that is the last value the
 *    server acknowledged. `commitField` on blur consults it, so a blur that did
 *    not change the value issues NO call (AT3) — the most common interaction in
 *    the whole feature is an editor tabbing through a form, and a naive
 *    save-on-blur gets exactly that case wrong.
 *
 *  • CHANGED-FIELDS-ONLY BODIES. This is a CROSS-SLICE CONTRACT, not an
 *    optimisation: `_touches_translatable` (backend `admin_quiz_content.py:58`)
 *    fires a Spanish retranslate whenever a translatable field is PRESENT in the
 *    body. The old `QuestionDetailForm.handleSave` deliberately sent every field
 *    on every save so retranslation would fire; under per-blur auto-save that
 *    would enqueue a full retranslate on every blur. The diff preserves
 *    retranslate-when-the-text-changed and drops it for XP/sort edits.
 *
 *  • `''` IS A VALUE, "absent" IS NOT. Dirtiness is tracked by KEY PRESENCE in a
 *    Set, never by truthiness of the value, and the body is built by copying
 *    `draft[key]` for each dirty key. So clearing a field to `''` sends
 *    `{field: ''}` and an untouched field is simply not a key. Nothing in this
 *    file may normalise one into the other in either direction — see
 *    `buildChangedBody` and the tests that pin it.
 *
 *  • A SERIALISED SAVE CHAIN. Every save is appended to one promise chain, so
 *    two rapid blurs in create mode cannot both take the create branch: the
 *    second one does not start until the first has resolved and set `idRef`
 *    (AT4). This is an invariant of the queue, not a lucky interleaving.
 *
 *  • RETRY, but only where a retry is safe. See AUTO_SAVE_RETRY_DELAYS_MS.
 *
 *  • FLUSH-ON-DEMAND, the entry point navigation uses (AT9). It resolves `false`
 *    when something is still unsaved, which is what holds the selection.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../../utils/api';

/** 1 initial attempt + 2 retries. Deliberately small: the editor is still
 *  sitting in the field, and a long silent retry loop reads as a hang. */
export const AUTO_SAVE_MAX_ATTEMPTS = 3;

/** Backoff before retry #2 and #3. Short for the same reason, and short enough
 *  that a component test can wait out a full exhaust (600 ms) inside the default
 *  `waitFor` budget without fake timers. */
export const AUTO_SAVE_RETRY_DELAYS_MS = [150, 450];

/** design/footer.html §2 — the spinner is held for a floor so a 60 ms save reads
 *  as a state rather than a flash. Display only: `flush()` still resolves as
 *  soon as the network settles, so navigation is never delayed by the floor. */
export const AUTO_SAVE_SPINNER_FLOOR_MS = 400;

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Which failure family the banner is in — design/error-states.html §"The one
 *  rule that decides which surface you get".
 *   • `save` / `create` / `auth` are `danger-*`: a fault the author cannot fix
 *     from inside the panel. `auth` earns its own lede because a plain Try again
 *     is a dead end after a 401.
 *   • `validation` is `attention-*`: the author typed it and can fix it. It is
 *     never sent, so it has no Try again — retrying is not the fix. */
export type AutoSaveErrorKind = 'save' | 'create' | 'auth' | 'validation';

export interface AutoSaveError<T extends object> {
  kind: AutoSaveErrorKind;
  /** The field KEYS, so the panel can put `border-danger-line` on the input. */
  fields: FieldOf<T>[];
  /** The same fields in the editor's own words — the banner names them. */
  fieldLabels: string[];
  /** For `validation` this is the app's own copy for the held field, and the
   *  resting banner DOES render it verbatim as its lede
   *  (`ItemDetailFooter.tsx`, `lede()`). For every other kind it is the thrown
   *  error's text and the banner writes its own words instead, so server detail
   *  never reaches the screen. `qa` re-check: the previous wording said "never
   *  rendered raw" without the first half, which the validation branch
   *  contradicts. */
  message: string;
  /** False for `validation`: offering a retry that cannot work is a lie. */
  retryable: boolean;
}

export type FieldOf<T> = keyof T & string;

export interface UseAutoSaveOptions<T extends object> {
  /** The server's current values. Also the initial draft. */
  initial: T;
  /** null = create mode; the row id once the row exists. */
  itemId: string | null;
  /** field → the label the error banner uses ("Question text"). */
  labels: Partial<Record<FieldOf<T>, string>>;
  /** Create the row. Receives the WHOLE draft — a create needs the required
   *  fields, not a diff. Must resolve to the new row's id. */
  create?: (draft: T) => Promise<string>;
  /** Update the row with ONLY the changed fields. */
  update: (id: string, changed: Partial<T>) => Promise<unknown>;
  /** Gates CREATE only, never an update (rehearsal's `role` resolves a NOT NULL
   *  FK, so a create without it cannot succeed — spec §Incomplete items save
   *  anyway makes everything ELSE advisory). Return a reason to block. */
  createGate?: (draft: T) => string | null;
  /** Holds EVERY write while some other operation owns the item — today only
   *  reading/video's in-flight image upload (brief §6.4: "do not fire a save
   *  mid-upload"). Return a reason to hold. Nothing is lost: the fields stay
   *  dirty, and the panel calls `flush()` when the hold lifts. Navigation is
   *  held too, which is correct — and "Leave anyway" is still the way out. */
  holdWrites?: () => string | null;
  /** Client-side mirror of a backend `ge`/`le`/cross-field bound that would 4xx.
   *  Returns one verdict per field that must be HELD BACK. The other fields in
   *  the same save still go out — design/error-states.html state E: "Only this
   *  field is held; every other field keeps auto-saving normally." */
  validateFields?: (fields: FieldOf<T>[], draft: T) => Array<{ field: FieldOf<T>; message: string }>;
  /** Fired after every ACKNOWLEDGED write. The panel's parent uses this to patch
   *  the middle-column row in place instead of reloading the drill (AT10). */
  onPersisted?: (info: { id: string; changed: Partial<T>; draft: T; created: boolean }) => void;
  /** Override the value comparison for exotic fields. */
  equals?: (a: unknown, b: unknown) => boolean;
  /** Name the failed fields for the banner. Defaults to `labels[field]`, one per
   *  field — which is right for a scalar and WRONG for a LIST field, where one
   *  key can stand for several things the editor sees separately. The rehearsal
   *  criteria list is the case: it is one key in the diff and two visible groups
   *  on screen. Receives the baseline so a describer can say WHICH entries
   *  changed. See `design/NOTES.md` §6b "Rehearsal criterion labels". */
  describeFields?: (fields: FieldOf<T>[], draft: T, baseline: T) => string[];
  /** Test seam. */
  retryDelaysMs?: number[];
  /** Test seam for AUTO_SAVE_SPINNER_FLOOR_MS. It exists because the floor and
   *  the in-flight counter (save-status.html §3②/§3④) MASK EACH OTHER: with the
   *  real 400 ms floor in play, deleting the counter left the burst test green,
   *  because the floor timer was cancelled by the next save before it could
   *  render `Saved` either way. Two mechanisms, one observable — so a test that
   *  means to pin the counter must first take the floor out of the picture. */
  spinnerFloorMs?: number;
}

export interface AutoSaveApi<T extends object> {
  draft: T;
  /** Local edit only — no write. Pair with `commitField` on blur. */
  setField: <K extends FieldOf<T>>(field: K, value: T[K]) => void;
  /** Local edit of several fields at once — no write. */
  setFields: (patch: Partial<T>) => void;
  /** Blur handler. Writes ONLY if the value differs from the last acknowledged
   *  one, so a no-op blur is silent (AT3). */
  commitField: (field: FieldOf<T> | FieldOf<T>[]) => void;
  /** Non-text controls (AT2): edit and write in the same gesture. */
  saveField: <K extends FieldOf<T>>(field: K, value: T[K]) => void;
  /** Non-text controls that move several fields at once. */
  saveFields: (patch: Partial<T>) => void;
  status: AutoSaveStatus;
  savedAt: number | null;
  error: AutoSaveError<T> | null;
  /** Why a create is being withheld, if it is (never blocks an update). */
  blockedReason: string | null;
  /** Write anything still pending. Resolves true only when nothing is left
   *  unsaved — navigation holds on false (AT9). */
  flush: () => Promise<boolean>;
  /** The banner's "Try again". Same engine as `flush`. */
  retry: () => Promise<boolean>;
  /** null until the first create resolves. */
  itemId: string | null;
  isDirty: boolean;
}

/** Structural equality that keeps `''`, `null` and `undefined` DISTINCT.
 *  `Object.is` already separates them; arrays and plain objects (the rehearsal
 *  criteria list, the reading images list) recurse. */
export function autoSaveEquals(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => autoSaveEquals(v, b[i]));
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const ka = Object.keys(a as object);
    const kb = Object.keys(b as object);
    if (ka.length !== kb.length) return false;
    return ka.every(
      (k) =>
        Object.prototype.hasOwnProperty.call(b, k) &&
        autoSaveEquals((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]),
    );
  }
  return false;
}

/** Build the request body from the dirty KEY SET. A key that is dirty is copied
 *  verbatim — including `''`. A key that is not dirty is absent. There is
 *  deliberately no filtering step here. */
export function buildChangedBody<T extends object>(draft: T, fields: FieldOf<T>[]): Partial<T> {
  const body: Partial<T> = {};
  fields.forEach((f) => {
    body[f] = draft[f];
  });
  return body;
}

/** Retry transport failures and 5xx. A 4xx is a rejected payload — repeating it
 *  produces the same 4xx and delays the error the editor needs to see. */
export function isRetryableSaveError(err: unknown): boolean {
  if (err instanceof ApiError) return err.status >= 500;
  return true;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useAutoSave<T extends object>(options: UseAutoSaveOptions<T>): AutoSaveApi<T> {
  const {
    initial,
    itemId,
    labels,
    create,
    update,
    createGate,
    holdWrites,
    validateFields,
    onPersisted,
    equals = autoSaveEquals,
    describeFields,
    retryDelaysMs = AUTO_SAVE_RETRY_DELAYS_MS,
    spinnerFloorMs = AUTO_SAVE_SPINNER_FLOOR_MS,
  } = options;

  const [draft, setDraft] = useState<T>(initial);
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<AutoSaveError<T> | null>(null);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(itemId);

  // Refs, not state: the save engine reads them from inside async callbacks that
  // were created before the render that would have refreshed a closure.
  const draftRef = useRef<T>(initial);
  const baselineRef = useRef<T>({ ...initial });
  const idRef = useRef<string | null>(itemId);
  const dirtyRef = useRef<Set<FieldOf<T>>>(new Set());
  const chainRef = useRef<Promise<boolean>>(Promise.resolve(true));
  /** How many saves are queued-or-running. save-status.html §3④ is explicit that
   *  this must be a COUNTER and not a boolean: a boolean is cleared by whichever
   *  response returns first and reports `Saved` while a write is still open. The
   *  status is a property of the PANEL — `saving` while any write is
   *  outstanding, `saved` only when the last one lands. */
  const pendingRef = useRef(0);
  const mountedRef = useRef(true);
  const floorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The engine's collaborators change identity every render; keep the latest.
  const cfgRef = useRef({ create, update, createGate, holdWrites, validateFields, onPersisted, equals, describeFields, retryDelaysMs, spinnerFloorMs, labels });
  cfgRef.current = { create, update, createGate, holdWrites, validateFields, onPersisted, equals, describeFields, retryDelaysMs, spinnerFloorMs, labels };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (floorTimerRef.current) clearTimeout(floorTimerRef.current);
    };
  }, []);

  const markDirty = useCallback((patch: Partial<T>) => {
    const eq = cfgRef.current.equals;
    (Object.keys(patch) as FieldOf<T>[]).forEach((f) => {
      if (eq(patch[f], baselineRef.current[f])) dirtyRef.current.delete(f);
      else dirtyRef.current.add(f);
    });
    setIsDirty(dirtyRef.current.size > 0);
  }, []);

  const setFields = useCallback(
    (patch: Partial<T>) => {
      draftRef.current = { ...draftRef.current, ...patch };
      setDraft(draftRef.current);
      markDirty(patch);
    },
    [markDirty],
  );

  const setField = useCallback(
    <K extends FieldOf<T>>(field: K, value: T[K]) => setFields({ [field]: value } as unknown as Partial<T>),
    [setFields],
  );

  /** One save attempt of whatever is currently dirty. Never throws; the boolean
   *  is "is everything we tried to write now acknowledged". */
  const performSave = useCallback(async (): Promise<boolean> => {
    const cfg = cfgRef.current;
    const fields = Array.from(dirtyRef.current);
    if (fields.length === 0) {
      if (mountedRef.current) setBlockedReason(null);
      return true;
    }

    const snapshot = { ...draftRef.current };

    const describe = (fs: FieldOf<T>[]): string[] =>
      cfg.describeFields?.(fs, snapshot, baselineRef.current) ?? fs.map((f) => cfg.labels[f] ?? f);

    const heldAll = cfg.holdWrites?.() ?? null;
    if (heldAll) {
      if (mountedRef.current) setBlockedReason(heldAll);
      return false;
    }

    // Held-back fields are NOT sent: the app already knows the server rejects
    // them, and firing a doomed request so a generic banner can offer a Try
    // again that cannot work is worse than not firing it at all
    // (design/error-states.html state E).
    const held = cfg.validateFields?.(fields, snapshot) ?? [];
    const heldFields = held.map((h) => h.field);
    const sendable = fields.filter((f) => !heldFields.includes(f));
    const raiseHeld = () => {
      if (!mountedRef.current) return;
      setStatus('error');
      setError({
        kind: 'validation',
        fields: heldFields,
        fieldLabels: describe(heldFields),
        message: held.map((h) => h.message).join(' '),
        retryable: false,
      });
    };
    if (sendable.length === 0) {
      if (held.length > 0) raiseHeld();
      return false;
    }

    const creating = idRef.current === null;
    if (creating) {
      const reason = cfg.createGate?.(snapshot) ?? null;
      if (reason) {
        // NOT an error state: nothing was attempted and nothing was lost. The
        // panel's own advisory already explains it; navigation still holds, and
        // "Leave anyway" is the way out (spec §When a save fails).
        if (mountedRef.current) setBlockedReason(reason);
        return false;
      }
      if (!cfg.create) return false;
    }
    if (mountedRef.current) setBlockedReason(null);

    if (floorTimerRef.current) {
      clearTimeout(floorTimerRef.current);
      floorTimerRef.current = null;
    }
    if (mountedRef.current) {
      setStatus('saving');
      setError(null);
    }
    const startedAt = Date.now();

    const settleDisplay = (next: AutoSaveStatus, at: number | null) => {
      const apply = () => {
        if (!mountedRef.current) return;
        setStatus(next);
        if (at !== null) setSavedAt(at);
      };
      const wait = Math.max(0, cfg.spinnerFloorMs - (Date.now() - startedAt));
      if (wait === 0) apply();
      else floorTimerRef.current = setTimeout(apply, wait);
    };

    const changed = buildChangedBody(snapshot, sendable);

    // Commit the fields we just wrote back into the baseline. A field the editor
    // kept typing into WHILE the write was in flight stays dirty, because its
    // current value no longer matches the snapshot the server acknowledged.
    const acknowledge = () => {
      sendable.forEach((f) => {
        baselineRef.current[f] = snapshot[f];
        if (cfg.equals(draftRef.current[f], snapshot[f])) dirtyRef.current.delete(f);
      });
      if (mountedRef.current) setIsDirty(dirtyRef.current.size > 0);
    };

    try {
      if (creating) {
        // A create is NOT retried. POST is not idempotent, and a transport
        // failure cannot distinguish "never arrived" from "row inserted, reply
        // lost" — retrying the second case duplicates the item. The editor gets
        // the error banner with the value intact and an explicit Try again.
        const newId = await cfg.create!(snapshot);
        idRef.current = newId;
        acknowledge();
        if (mountedRef.current) setCurrentId(newId);
        cfg.onPersisted?.({ id: newId, changed, draft: snapshot, created: true });
      } else {
        let attempt = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          try {
            await cfg.update(idRef.current!, changed);
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= AUTO_SAVE_MAX_ATTEMPTS || !isRetryableSaveError(err)) throw err;
            await sleep(cfg.retryDelaysMs[attempt - 1] ?? 0);
          }
        }
        acknowledge();
        cfg.onPersisted?.({ id: idRef.current!, changed, draft: snapshot, created: false });
      }
      if (held.length > 0) {
        // Some fields landed, one is still held back. The panel is NOT saved.
        raiseHeld();
        return false;
      }
      // save-status.html §3④ — never tick back to `Saved` between two writes of
      // one burst. `pendingRef` still counts this save, so >1 means another is
      // already queued behind it and the spinner stays up.
      if (pendingRef.current > 1) return true;
      const at = Date.now();
      settleDisplay('saved', at);
      return true;
    } catch (err) {
      // The typed value is NEVER reverted: `draftRef` is untouched here and the
      // baseline is not advanced, so the field stays dirty and Try again resends.
      if (mountedRef.current) {
        const expired = err instanceof ApiError && (err.status === 401 || err.status === 403);
        setStatus('error');
        setError({
          kind: expired ? 'auth' : creating ? 'create' : 'save',
          fields: sendable,
          fieldLabels: describe(sendable),
          message: err instanceof Error ? err.message : String(err),
          retryable: true,
        });
      }
      return false;
    }
  }, []);

  /** Append to the single save chain. Serialising is what makes AT4's
   *  two-rapid-blurs case an invariant rather than a race: the second save
   *  cannot begin until the first has set `idRef`. */
  const enqueue = useCallback((): Promise<boolean> => {
    pendingRef.current += 1;
    const run = async () => {
      try {
        return await performSave();
      } finally {
        pendingRef.current -= 1;
      }
    };
    const next = chainRef.current.then(run, run);
    chainRef.current = next;
    return next;
  }, [performSave]);

  /** Blur. There is deliberately NO short-circuit here on "is this field dirty".
   *  An early return would be a SECOND place that decides whether a no-op blur
   *  writes, and two guards mask each other: with both in place, breaking the
   *  dirty set left every AT3 test green (measured — the naive
   *  save-every-field mutation reddened 25 tests and not one of the three AT3
   *  ones). ONE mechanism decides, and it is the dirty set inside `performSave`;
   *  a clean blur costs a microtask and issues no call. */
  const commitField = useCallback(
    (_field: FieldOf<T> | FieldOf<T>[]) => {
      void enqueue();
    },
    [enqueue],
  );

  /** Non-text controls. Same rule as `commitField`: no second dirtiness guard. */
  const saveFields = useCallback(
    (patch: Partial<T>) => {
      setFields(patch);
      void enqueue();
    },
    [setFields, enqueue],
  );

  const saveField = useCallback(
    <K extends FieldOf<T>>(field: K, value: T[K]) => saveFields({ [field]: value } as unknown as Partial<T>),
    [saveFields],
  );

  const flush = useCallback(() => enqueue(), [enqueue]);

  return {
    draft,
    setField,
    setFields,
    commitField,
    saveField,
    saveFields,
    status,
    savedAt,
    error,
    blockedReason,
    flush,
    retry: flush,
    itemId: currentId,
    isDirty,
  };
}

/**
 * The contract between a detail panel and the editor shell around it. Every one
 * of the five panels takes exactly this, so the shell's wiring is one code path
 * rather than five (AT11 is parametrised over the five for the same reason).
 */
export interface PanelSaveWiring {
  /** The panel registers its `flush` here on mount and `null` on unmount, so
   *  the shell can run it BEFORE it changes the selection. The `key` props on
   *  these panels force a remount on selection change; AT9's flush must happen
   *  BEFORE that remount, not race it, which is why the shell awaits this and
   *  only then calls `setSel`. */
  registerFlush?: (flush: (() => Promise<boolean>) | null) => void;
  /** True while the shell is HOLDING a selection change because this panel's
   *  flush resolved false. The panel escalates its own banner in place. */
  pendingNavigation?: boolean;
  /** The panel calls this when the hold is resolved — the retry landed, or the
   *  editor chose "Leave anyway" and abandoned the change. */
  onNavigationProceed?: () => void;
  /** An ACKNOWLEDGED auto-save. The shell patches the middle-column row in
   *  place with `changed`. It must NOT call `onReload()` here: on every blur
   *  that is a re-render storm and a focus thief, and AT10 exists to prevent
   *  exactly that. Create and delete still reload. */
  onPatched?: (id: string, changed: Record<string, unknown>) => void;
}

export default useAutoSave;
